import React from 'react';
import Link from 'components/Link';
import { IconName, IconColor } from 'components/Icon';
import Row from 'components/Row';
import TextWithIcon, { Props as TextWithIconProps } from 'components/TextWithIcon';

import 'coaching/components/styles/CoachingIsolatedView.scss';
import 'coaching/components/styles/Breadcrumb.scss';

import { STATES } from 'lib/TutoringStateMachine/TutoringStateMachine';
import CurrentUser from 'auth/CurrentUser';
import { compact, last } from 'lodash';
import CoachingConstants from 'coaching/CoachingConstants';

export type BreadcrumbSteps = 'Start' | 'Details' | 'Schedule' | 'Billing' | 'Summary' | 'Finished';

export const stepsToRoutes: { [key in BreadcrumbSteps]: string | null } = {
  Start: CoachingConstants.TUTORING_GET_STARTED,
  Details: CoachingConstants.TUTORING_STUDENT_DETAILS,
  Schedule: CoachingConstants.TUTORING_SCHEDULE,
  Billing: CoachingConstants.TUTORING_BILLING,
  Summary: CoachingConstants.TUTORING_LINK_STUDENT,
  Finished: null,
};

// The order of each step in the breadcrumbs
const stepOrdering: BreadcrumbSteps[] = [
  'Start',
  'Details',
  'Schedule',
  'Billing',
  'Summary',
  'Finished',
];

const statesToSteps: { [key in STATES]: BreadcrumbSteps } = {
  [STATES.GET_STARTED_AUTH]: 'Start',
  [STATES.GET_STARTED_UNAUTH]: 'Start',
  [STATES.STUDENT_DETAILS_UNAUTH]: 'Details',
  [STATES.SCHEDULE]: 'Schedule',
  [STATES.BILLING]: 'Billing',
  [STATES.LINK_STUDENT]: 'Summary',
  [STATES.SUMMARY]: 'Finished',
};

export type Props = {
  stepSelected: (step: BreadcrumbSteps) => void;
  stepConfig: StepConfig[];
};

export type StepConfig = {
  step: BreadcrumbSteps;
  // Whether the step is selectable(clickable)
  selectable: boolean;
  // Whether the step should show as greyed out
  greyedOut: boolean;
  // Whether the step is the one that's curently being viewed
  currentlySelected: boolean;
  // Whether the step has already been completed successfully
  completed: boolean;
  // Whether the step is the one that must be completed next to advance
  isFirstUncompleted: boolean;
};

export default function TutoringSelfServeViewBreadcrums(props: Props) {
  return (
    <Row>
      <div className="Breadcrumb">
        {props.stepConfig.map(config => {
          return (
            <BreadCrumb key={config.step} stepConfig={config} onSelected={() => props.stepSelected(config.step)} />
          );
        })}
      </div>
    </Row>
  );
}

function BreadCrumb(props: { stepConfig: StepConfig; onSelected: () => void }) {
  const { stepConfig } = props;
  const linkColor = !stepConfig.greyedOut ? 'ink' : 'graphite';

  const textAndIcon = (
    <TextWithIcon
      underline={stepConfig.currentlySelected}
      {...getIconStyles(stepConfig)}
      gutter="xxsmall"
      color={linkColor}
      weight={stepConfig.currentlySelected ? 'semibold' : 'normal'}>
      {stepConfig.step}
    </TextWithIcon>
  );
  return (
    <>
      {stepConfig.selectable && (
        <Link key={stepConfig.step} color={linkColor} data-test="breadcrumb-link" className="Breadcrumb-link" onClick={() => props.onSelected()}>
          {textAndIcon}
        </Link>
      )}
      {!stepConfig.selectable && (
        <div color={linkColor} data-test="breadcrumb-link-disabled" className="Breadcrumb-link Breadcrumb-link-disabled">
          {textAndIcon}
        </div>
      )}
    </>
  );
}

function getIconStyles(
  stepConfig: StepConfig
): Pick<TextWithIconProps, 'icon' | 'iconColor' | 'iconSize'> {
  let icon: IconName;
  let iconColor: IconColor;
  if (stepConfig.completed) {
    icon = 'progress-complete';
    iconColor = 'ink';
  } else if (stepConfig.isFirstUncompleted) {
    icon = 'progress-active';
    iconColor = 'ink';
  } else {
    icon = 'progress-incomplete';
    iconColor = 'graphite';
  }

  return { icon, iconColor, iconSize: 'medium' };
}

function isEarlier(step: BreadcrumbSteps, otherStep: BreadcrumbSteps) {
  return stepOrdering.indexOf(step) < stepOrdering.indexOf(otherStep);
}

function isLater(step: BreadcrumbSteps, otherStep: BreadcrumbSteps) {
  return stepOrdering.indexOf(step) > stepOrdering.indexOf(otherStep);
}

export function getBreadcrumbConfig(currentState: STATES, enabledStates: STATES[]) {
  const currentActiveStep = statesToSteps[currentState];
  const completedSteps = enabledStates.map(s => statesToSteps[s]);
  const firstUncompletedStep = last(completedSteps)!;

  const billingCompleted = isLater(firstUncompletedStep, 'Billing');
  const isSignedIn = CurrentUser.isSignedIn();

  function configForStep(step: BreadcrumbSteps): StepConfig {
    const selectable =
      !(currentActiveStep === step) &&
      (completedSteps.includes(step) || firstUncompletedStep === step) &&
      !billingCompleted; // Prevent any nav once billing step is done
    let completed = isEarlier(step, firstUncompletedStep);
    const currentlySelected = step === currentActiveStep;
    return {
      step,
      selectable,
      greyedOut: !selectable && !currentlySelected && !completed,
      isFirstUncompleted: step === last(completedSteps),
      completed,
      currentlySelected: step === currentActiveStep,
    };
  }

  return compact([
    configForStep('Start'),
    !isSignedIn && configForStep('Details'),
    configForStep('Schedule'),
    configForStep('Billing'),
    configForStep('Summary'),
  ]);
}
